<template>
    <div>
        <Loader :field="'companies'" v-if="loadingScoutedJobs || listLoading" />
        <div v-else class="job-list">
            <!-- Scouted Jobs Section -->
            <div class="job-section">
                <div class="section-header" @click="toggleScoutedJobs">
                    <h3 class="section-title">あなたがスカウト希望した求人</h3>
                    <span class="collapse-icon">{{ scoutedJobsCollapsed ? '▼' : '▲' }}</span>
                    <!-- <i :class="scoutedJobsCollapsed ? 'fa fa-chevron-down' : 'fa fa-chevron-up'" class="collapse-icon"></i> -->
                </div>
                <div v-show="!scoutedJobsCollapsed" class="section-content">
                    <div class="scout-status p-1">
                        スカウト希望済み{{ scoutedJobs.length }}件、<span class="remaining-count">残り{{ 5 - scoutedJobs.length }}件可能</span>
                    </div>
                    <JobListItem
                        v-for="(job, index) in scoutedJobs"
                        :key="`scouted-${job._id}`"
                        :job="job"
                        :isJobScouted="true"
                        :index="index"
                        :searchHighlights="job.highlights || []"
                        @click="getJobsToScout(job, index, 'scouted')"
                    />
                </div>
            </div>

            <!-- Other Jobs Section -->
            <div class="job-section">
                <div class="section-header" @click="toggleOtherJobs">
                    <h3 class="section-title">その他の求人</h3>
                    <span class="collapse-icon">{{ otherJobsCollapsed ? '▼' : '▲' }}</span>
                    <!-- <i :class="otherJobsCollapsed ? 'fa fa-chevron-down' : 'fa fa-chevron-up'" class="collapse-icon"></i> -->
                </div>
                <div v-show="!otherJobsCollapsed" class="section-content">
                    <!-- Toggle component -->
                    <div class="toggle-container p-2">
                        <span class="toggle-label">マッチ求人のみ表示 </span>
                        <toggle-button
                            v-model="localShowMatchedJobs"
                            :sync="true"
                            :labels="false"
                            :width="50"
                            :height="24"
                        />
                    </div>

                    <!-- Search component - only show when toggle is OFF -->
                    <div v-if="!showMatchedJobs" class="search-row p-2">
                        <div class="search-label">フリーワード</div>
                        <input
                            type="text"
                            class="search-input"
                            v-model="localSearchKeyword"
                            placeholder="複数ワードはスペース区切りで入力"
                        />
                        <button class="search-button" @click="handleSearch">
                            検索
                        </button>
                    </div>

                    <JobListItem
                        v-for="(job, index) in displayedOtherJobs"
                        :key="`other-${job._id}`"
                        :job="job"
                        :isJobScouted="isJobScoutedById(job._id)"
                        :index="index"
                        :searchHighlights="job.highlights || []"
                        @click="getJobsToScout(job, index, 'other')"
                    />
                    
                    <!-- Pagination inside collapsible -->
                    <div class="pagination-area" v-if="totalPages > 1 && !showMatchedJobs && otherJobs.length > 0">
                        <Pagination
                            :total-page="totalPages"
                            :callback="handlePageChange"
                            :currentPage="currentPage"
                        />
                    </div>
                </div>
            </div>
        </div>

        <CompanySelectionModal
            ref="companySelectionModal"
            :companyName="selectedJobCompany"
            :companyId="selectedJob.company_id"
            :companyDescription="selectedCompany && selectedCompany.short_description"
            :loading="loading"
            @proceed="handleCompanySelectionProceed"
            @cancel="handleCompanySelectionCancel"
        />

        <TermsConditionsModal
            ref="termsConditionsModal"
            @agree="handleTermsAgree"
            @cancel="handleTermsCancel"
        />

        <JobDescriptionModal
            ref="jobModal"
            :currentJob="selectedJob"
            :currentIndex="actualJobIndex"
            :totalJobs="totalJobsCount"
            :isJobScouted="isSelectedJobScouted"
            :hasReachedScoutLimit="hasReachedScoutLimit"
            :loading="loadingJobDetails"
            :company="profile.company_name"
            @scout="openScoutConfirmation"
            @prev="handlePrev"
            @next="handleNext"
        />
        <ScoutConfirmationModal
            ref="scoutConfirmModal"
            @confirm="checkSharedProfile"
            @cancel="handleScoutCancel"
            :loading="loading"
        />

        <!-- Success Modal after scout submission -->
        <BaseModal
            v-if="showScoutSuccess"
            ref="scoutSuccessModal"
            :modal-id="'scoutSuccessModal'"
            :modal-text="'スカウト希望が完了しました！'"
            :button-text="'OK'"
            @button-click="handleScoutSuccessOk"
        />
    </div>
</template>

<script>
import JobDescriptionModal from "./JobDescriptionModal.vue";
import ScoutConfirmationModal from "./ScoutConfirmationModal.vue";
import CompanySelectionModal from "./CompanySelectionModal.vue";
import TermsConditionsModal from "./TermsConditionsModal.vue";
import BaseModal from "./BaseModal.vue";
import JobListItem from "./JobListItem.vue";
import Loader from "../Loader.vue";
import Api from "../../config/api";
import Pagination from "../Pagination.vue";
import { mapGetters, mapActions, mapMutations } from "vuex";
export default {
    name: "JobLists",
    components: {
        JobDescriptionModal,
        ScoutConfirmationModal,
        CompanySelectionModal,
        TermsConditionsModal,
        BaseModal,
        JobListItem,
        Loader,
        Pagination,
    },
    props: {
        showMatchedJobs: {
            type: Boolean,
            required: true,
        },
        searchKeyword: {
            type: String,
            default: "",
        },
        companies: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            filteredJobs: [],
            jobs: [],
            scoutedJobs: [],
            allJobs: [],
            loading: false,
            loadingScoutedJobs: false,
            selectedJob: {},
            jobsByCompany: [], // Jobs from the same company
            currentJobIndex: 0,
            loadingJobDetails: false,
            showScoutConfirmation: false,
            clientPage: 1,
            pageSize: 20,
            scoutedJobsCollapsed: false,
            otherJobsCollapsed: false,
            selectedJobType: 'other', // 'scouted' or 'other'
            showScoutSuccess: false,
        };
    },
    computed: {
        ...mapGetters({
            profile: 'getProfile',
            serverJobs: 'jobs/getJobs',
            totalJobs: 'jobs/getTotalJobs',
            currentPage: 'jobs/getCurrentPage',
            totalPages: 'jobs/getTotalPages',
            listLoading: 'jobs/getLoading'
        }),
        // Add computed property for two-way binding
        localShowMatchedJobs: {
            get() {
                return this.showMatchedJobs;
            },
            set(value) {
                this.$emit('toggle-change', value);
            }
        },
        selectedJobCompany() {
            return this.selectedJob && this.selectedJob.companyName 
        },
        selectedCompany() {
            const companyId = this.selectedJob && this.selectedJob.company_id;
            if (!companyId || !this.companies || !this.companies.length) return null;
            return this.companies.find(c => c._id === companyId) || null;
        },
        otherJobs() {
            if (this.showMatchedJobs) {
                // Filter out scouted jobs from the main jobs list
                const scoutedJobIds = this.scoutedJobs.map(job => job._id);
                return this.filteredJobs.filter(job => !scoutedJobIds.includes(job._id));
            }
            return this.serverJobs;
        },
        displayedOtherJobs() {
            if (this.showMatchedJobs) {
                const start = (this.clientPage - 1) * this.pageSize;
                const end = start + this.pageSize;
                return this.otherJobs.slice(start, end);
            }
            return this.otherJobs;
        },
        totalJobsCount() {
            if (this.selectedJobType === 'scouted') {
                return this.scoutedJobs.length;
            }
            return this.showMatchedJobs ? this.otherJobs.length : this.totalJobs;
        },
        actualJobIndex() {
            if (this.selectedJobType === 'scouted') {
                return this.currentJobIndex;
            }
            if (this.showMatchedJobs) {
                return this.currentJobIndex;
            }
            // Calculate actual index based on current page (20 items per page)
            return ((this.currentPage - 1) * 20) + this.currentJobIndex;
        },
        isCurrentJobScouted() {
            return this.selectedJobType === 'scouted';
        },
        isSelectedJobScouted() {
            const id = this.selectedJob && this.selectedJob._id;
            if (!id) return false;
            return this.scoutedJobs.some(j => j._id === id);
        },
        hasReachedScoutLimit() {
            return this.scoutedJobs.length >= 5;
        },
    },
    methods: {
        ...mapActions({
            loadJobs: 'jobs/loadJobs'
        }),
        ...mapMutations({
            setPage: 'setPage'
        }),
        toggleScoutedJobs() {
            this.scoutedJobsCollapsed = !this.scoutedJobsCollapsed;
        },
        toggleOtherJobs() {
            this.otherJobsCollapsed = !this.otherJobsCollapsed;
        },
        async handleSearch() {
            if (this.showMatchedJobs) {
                // Perform client-side search on existing jobs
                this.performClientSearch();
            } else {
                // Perform server-side search
                await this.performServerSearch();
            }
        },
        performClientSearch() {
            if (!this.localSearchKeyword.trim()) {
                // If search is empty, show all jobs
                this.filteredJobs = this.jobs;
                return;
            }
            const searchTerms = this.localSearchKeyword
                .toLowerCase()
                .split(/[,\s]+/)
                .filter((term) => term);

            // Filter jobs that match ALL search terms
            this.filteredJobs = this.jobs.filter((job) => {
                // Create a single searchable text from relevant job fields
                const searchableText = [
                    job.job_title,
                    job.job_description,
                    job.location,
                ]
                    .join(" ")
                    .toLowerCase();

                // Job must match ALL search terms to be included
                return searchTerms.every((term) =>
                    searchableText.includes(term)
                );
            });
        },
        async handlePageChange(page) {
            this.scrollToTop();
            if (this.showMatchedJobs) {
                this.clientPage = page;
            } else {
                this.setPage(page);
                await this.loadJobs({ page, searchKeyword: this.localSearchKeyword });
            }
        },
        async performServerSearch() {
            await this.loadJobs({ page: 1, searchKeyword: this.localSearchKeyword });
        },
        getJobsToScout(job, index, jobType) {
            // Find the index of the clicked job in the appropriate list
            this.currentJobIndex = index;
            this.selectedJobType = jobType;
            if (this.currentJobIndex === -1) this.currentJobIndex = 0;

            // Set the selected job
            this.selectedJob = job;

            // Show the modal
            this.$refs.jobModal.show();
        },
        async handlePrev() {
            let currentJobs = this.selectedJobType === 'scouted' ? this.scoutedJobs : this.displayedOtherJobs;
            
            // If there are previous jobs in current section, move to previous
            if (this.currentJobIndex > 0) {
                this.currentJobIndex = this.currentJobIndex - 1;
                this.selectedJob = currentJobs[this.currentJobIndex];
                return;
            }
            
            // Handle beginning of current section based on job type
            switch (this.selectedJobType) {
                case 'scouted':
                    console.log('first scouted job - closing modal');
                    this.$refs.jobModal.hide();
                    break;
                    
                case 'other':
                    if (!this.showMatchedJobs && this.currentPage > 1) {
                        // Load previous page for paginated jobs
                        const prevPage = this.currentPage - 1;
                        this.setPage(prevPage);
                        await this.loadJobs({ page: prevPage, searchKeyword: this.localSearchKeyword });
                        // Set index to last job of the previous page
                        this.currentJobIndex = this.displayedOtherJobs.length - 1;
                        this.selectedJob = this.displayedOtherJobs[this.currentJobIndex];
                        this.scrollToTop();
                    } else {
                        // Close modal for matched jobs or when no previous pages
                        console.log('closing modal - no previous other jobs');
                        this.$refs.jobModal.hide();
                    }
                    break;
                    
                default:
                    console.log('closing modal - unknown job type');
                    this.$refs.jobModal.hide();
            }
        },
        async handleNext() {
            let currentJobs = this.selectedJobType === 'scouted' ? this.scoutedJobs : this.displayedOtherJobs;
            
            
            // If there are more jobs in current section, move to next
            if (this.currentJobIndex < currentJobs.length - 1) {
                this.currentJobIndex = this.currentJobIndex + 1;
                this.selectedJob = currentJobs[this.currentJobIndex];
                return;
            }
            
            // Handle end of current section based on job type
            switch (this.selectedJobType) {
                case 'scouted':
                    console.log('last scouted job - closing modal');
                    this.$refs.jobModal.hide();
                    break;
                    
                case 'other':
                    if (!this.showMatchedJobs && this.currentPage < this.totalPages) {
                        // Load next page for paginated jobs;
                        const nextPage = this.currentPage + 1;
                        this.setPage(nextPage);
                        await this.loadJobs({ page: nextPage, searchKeyword: this.localSearchKeyword });
                        this.currentJobIndex = 0;
                        this.selectedJob = this.displayedOtherJobs[this.currentJobIndex];
                        this.scrollToTop();
                    } else {
                        // Close modal for matched jobs or when no more pages
                        console.log('closing modal - no more other jobs');
                        this.$refs.jobModal.hide();
                    }
                    break;
                    
                default:
                    console.log('closing modal - unknown job type');
                    this.$refs.jobModal.hide();
            }
        },
        openScoutConfirmation() {
            this.showScoutConfirmation = true;
            this.$nextTick(() => {
                this.$refs.scoutConfirmModal && this.$refs.scoutConfirmModal.show();
            });
        },
        handleScoutCancel() {
            this.showScoutConfirmation = false;
            this.$nextTick(() => {
                this.$refs.scoutConfirmModal.hide();
            });
        },
        handleCompanySelectionProceed() {
            // Proceed to terms modal
            if (this.$refs.termsConditionsModal) {
                this.$refs.termsConditionsModal.show();
            }
        },
        handleCompanySelectionCancel() {
            if (this.$refs.companySelectionModal) {
                this.$refs.companySelectionModal.hide();
            }
        },
        handleTermsAgree() {
            // Submit and close
            this.submitScoutRequest(this.selectedJob, this.selectedJob.company_id);
            if (this.$refs.termsConditionsModal) {
                this.$refs.termsConditionsModal.hide();
            }
        },
        handleTermsCancel() {
            if (this.$refs.termsConditionsModal) {
                this.$refs.termsConditionsModal.hide();
            }
        },
        checkSharedProfile(){
            const companyId = this.selectedJob && this.selectedJob.company_id;
            const hasSharedProfile = this.profile && this.profile.other_companies && this.profile.other_companies.includes(companyId);
            if (!hasSharedProfile) {
                this.$refs.scoutConfirmModal && this.$refs.scoutConfirmModal.hide();
                this.showScoutConfirmation = false;
                if (this.$refs.companySelectionModal) {
                    this.$refs.companySelectionModal.show();
                }
            } else {
                this.handleScout();
            }
        },
        async handleScout() {
            try {
                this.loading = true;
                await this.submitScoutRequest(this.selectedJob);
                this.$nextTick(() => {
                    // this.$refs.scoutConfirmModal.hide();

                    this.$refs.scoutConfirmModal && this.$refs.scoutConfirmModal.hide();
                    this.showScoutConfirmation = false;
                    // this.handleNext();
                });
            } catch (error) {
                console.error("Scout request failed:", error);
                // Handle error (show error message, etc.)
            } finally {
                this.loading = false;
            }
        },
        async submitScoutRequest(jobId, companyToShare=null) {
            // Implement your API call here
            try {
                const token = await this.$auth.getToken();
                const headers = {
                    Authorization: `Bearer ${token}`,
                };
                const { _id, company_id } = jobId;
                await this.axios({
                    method: "post",
                    url: Api.url(Api.endpoints.addToScout),
                    headers,
                    data: {
                        jobId: _id,
                        jobCompany: company_id,
                        companyToShare,
                    },
                });
                const scoutedJob = this.displayedOtherJobs.find(
                    (job) => job._id === jobId
                );
                const jobToPush = scoutedJob || this.selectedJob || null;
                if (
                    jobToPush &&
                    !this.scoutedJobs.some((job) => job._id === jobId)
                ) {
                    this.scoutedJobs.push(jobToPush);
                }
                // Show success modal
                this.$refs.companySelectionModal && this.$refs.companySelectionModal.hide();
                this.showScoutSuccess = true;
            } catch (err) {
                console.log("Submitting scout request for job:", jobId);
            }
        },
        isJobScoutedById(jobId) {
            return this.scoutedJobs.some(job => job._id === jobId);
        },
        handleScoutSuccessOk() {
            // Close success modal
            this.showScoutSuccess = false;
        },
        async getUserBasedJobs() {
            try {
                this.loadingScoutedJobs = true;
                const token = await this.$auth.getToken();
                const headers = {
                    Authorization: `Bearer ${token}`,
                };
                const jobs = await this.axios({
                    method: "get",
                    url: Api.url(Api.endpoints.allJobs),
                    headers,
                });
                //   console.log(jobs);
                this.filteredJobs = jobs.data;
                this.jobs = jobs.data;
            } catch (err) {
                console.error("Error fetching jobs:", err);
            } finally {
                this.loadingScoutedJobs = false;
            }
        },
        async getJobseekerScoutedJobs() {
            try {
                this.loadingScoutedJobs = true;
                const token = await this.$auth.getToken();
                const headers = {
                    Authorization: `Bearer ${token}`,
                };
                const jobs = await this.axios({
                    method: "get",
                    url: Api.url(Api.endpoints.scoutedJobs),
                    headers,
                });
                this.scoutedJobs = jobs.data;
                // console.log(this.scoutedJobs);
            } catch (err) {
                console.error("Error fetching jobs:", err);
            } finally {
                this.loadingScoutedJobs = false;
            }
        },
        scrollToTop() {
            window.scrollTo(0, 0);
        },
    },
    async created() {
        await this.getUserBasedJobs();
        await this.getJobseekerScoutedJobs();
        if (!this.showMatchedJobs) {
            await this.loadJobs({ page: 1 });
        }
    },
    watch: {
        showMatchedJobs: {
            handler(newVal) {
                if (!newVal) {
                    this.performServerSearch();
                }
            },
        },
        // Keep localSearchKeyword in sync with prop and notify parent on changes
        searchKeyword: {
            immediate: true,
            handler(newVal) {
                this.localSearchKeyword = newVal || '';
            }
        },
        localSearchKeyword(newVal) {
            if (newVal !== this.searchKeyword) {
                this.$emit('search', newVal);
            }
        }
    },
};
</script>

<style scoped>
.jobs {
    justify-content: space-between;
}
.job-list {
    display: flex;
    flex-direction: column;
}

.job-section {
    margin-bottom: 20px;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background-color: #ffffff;
    /* border: 1px solid #e9ecef; */
    /* border-radius: 4px; */
    cursor: pointer;
    transition: background-color 0.2s ease;
}


.header-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.section-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #000000;
}

.collapse-icon {
    font-size: 14px;
    color: black;
    transition: transform 0.2s ease;
}

.section-content {
    margin-top: 8px;
}

.job-card {
    background-color: white;
    border: 1px solid #e0e0e0;
    border-radius: 1px;
    padding: 16px;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.job-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.company-name {
    font-weight: bold;
    font-size: 16px;
    color: #3c3c3c;
    font-weight: 600;
    margin-bottom: 8px;
}

.job-title {
    font-size: 14px;
    font-weight: 600;
    color: #3c3c3c;
}
.scouted-badge {
    margin: auto 0px;
}

.scouted-badge-text {
    width: 60px;
    border: 1px solid #fea403;
    color: #fea403;
    text-align: center;
    font-weight: bold;
    font-size: 10px;
}

/* Toggle container styles */
.toggle-container {
    display: flex;
    align-items: center;
    /* margin-bottom: 15px; */
    font-size: 12px;
}

.toggle-label {
    margin-right: 10px;
}

.search-row {
    display: flex;
    align-items: center;
    margin-bottom: 15px;
}

.search-label {
    margin-right: 15px;
    min-width: 80px;
    white-space: nowrap;
    font-size: 12px;
}

.search-input {
    padding: 8px 12px;
    border: 1px solid #ccc;
    border-radius: 4px;
    background-color: #ffffff;
    margin-right: 10px;
    font-size: 12px;
    width: 300px;
}

.search-input::placeholder {
    font-size: 12px;
    color: #666;
}

.search-button {
    padding: 8px 12px;
    background-color: #75C791;
    border: 1px solid #ccc;
    border-radius: 4px;
    cursor: pointer;
    white-space: nowrap;
    font-size: 12px;
    color: #fff;
    width: 53px;
}

.scout-status {
    font-size: 12px;
    color: #3c3c3c;
    margin: 8px 16px 8px 16px;
    padding: 0;
}

.remaining-count {
    color: #ff0000;
    font-weight: bold;
}
</style>
